import React, { useState, useEffect } from 'react';
import ContentSelectorMulti from '../components/ContentSelectorMulti';
import {
  Card,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  message,
  Row,
  Col,
  Space,
  Typography,
  Table,
  Tag,
  Popconfirm,
  Modal,
  Descriptions,
  Divider,
  Spin,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
  SafetyOutlined,
  CalendarOutlined,
  DesktopOutlined,
  PlayCircleOutlined,
  FormOutlined,
  EditOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import { adminService, License, CreateLicenseData, UpdateLicenseData, Content, LicenseItem as ApiLicenseItem, PaginatedResponse } from '../services/admin';
import BulkGenerationModal from '../components/BulkGenerationModal';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

interface DraftLicenseItem {
  id?: number;
  fileId: string;
  contentType: string;
  validUntil: string;
  maxPlayCount: number;
  encryptedKey?: string;
  signature?: string;
}

interface BulkLicenseItem {
  fileId: string;
  contentType: string;
  validUntil: string;
  maxPlayCount: number;
  encryptedKey?: string;
  signature?: string;
}

interface LicenseEditProps {
  mode: 'create' | 'edit';
  licenseId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const LicenseEdit: React.FC<LicenseEditProps> = ({ mode, licenseId, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [licenseItems, setLicenseItems] = useState<DraftLicenseItem[]>([]);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [bulkModalVisible, setBulkModalVisible] = useState(false);

  // Lifecycle debugging
  useEffect(() => {
    console.log('LicenseEdit component mounted');
    return () => console.log('LicenseEdit component unmounted');
  }, []);

  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [itemForm] = Form.useForm();
  const [selectedMultiItems, setSelectedMultiItems] = useState<Content[]>([]);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPageSize, setItemsPageSize] = useState(10);

  const isEditMode = mode === 'edit' && licenseId;

  // Fetch license details for edit mode
  const { data: existingLicenseData, isLoading: licenseLoading } = useQuery<License | null>({
    queryKey: ['license', licenseId],
    queryFn: () => isEditMode ? adminService.getLicenseSummaryById(licenseId!) : null,
    enabled: !!isEditMode,
  });

  const {
    data: licenseItemsPaged,
    isLoading: licenseItemsLoading,
    refetch: refetchLicenseItems,
  } = useQuery<PaginatedResponse<ApiLicenseItem>>({
    queryKey: ['license-items-paged', licenseId, itemsPage, itemsPageSize],
    queryFn: () => adminService.getLicenseItemsPaged(licenseId!, itemsPage, itemsPageSize),
    enabled: !!isEditMode,
  });

  // Fetch users for the select dropdown
  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: adminService.getUsers,
  });

  // Fetch contents for the license item dropdown
  const { data: contentsResponse } = useQuery({
    queryKey: ['contents'],
    queryFn: () => adminService.getContents({ limit: 100 }),
  });

  // License creation mutation
  const createLicenseMutation = useMutation({
    mutationFn: (data: CreateLicenseData) => adminService.createLicense(data),
    onSuccess: (data) => {
      message.success('License created successfully!');
      if (onSuccess) onSuccess();
    },
    onError: (error: any) => {
      console.error('License creation failed:', error);
      message.error(`Failed to create license: ${error.response?.data?.message || error.message}`);
    }
  });

  const updateLicenseMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateLicenseData }) =>
      adminService.updateLicense(id, data),
    onSuccess: () => {
      message.success('License updated successfully!');
    },
    onError: (error: any) => {
      console.error('License update failed:', error);
      message.error(`Failed to update license: ${error.response?.data?.message || error.message}`);
    }
  });

  const clearLicenseItemsMutation = useMutation({
    mutationFn: (id: number) => adminService.clearLicenseItems(id),
    onSuccess: async (data) => {
      message.success(`Cleared ${data.deletedCount} license item(s)`);
      setItemsPage(1);
      await refetchLicenseItems();
    },
    onError: (error: any) => {
      console.error('Clear license items failed:', error);
      message.error(error.response?.data?.message || 'Failed to clear license items');
    }
  });

  // Populate form with existing data for edit mode
  useEffect(() => {
    if (existingLicenseData && isEditMode) {
      form.setFieldsValue({
        userId: existingLicenseData.userId,
        validUntil: dayjs(existingLicenseData.validUntil),
        maxDevices: existingLicenseData.maxDevices,
        maxConcurrentPlays: existingLicenseData.maxConcurrentPlays,
        maxPlayCount: existingLicenseData.maxPlayCount,
        description: existingLicenseData.description,
      });
    }
  }, [existingLicenseData, isEditMode, form]);

  const handleAddItem = () => {
    // For diagnostics: Add both manual and modal approaches
    console.log('Add File button clicked - using modal approach');

    // Show Ant Design Modal (primary method)
    setEditingItemIndex(null);
    setEditingItemId(null);

    try {
      // Reset form first
      itemForm.resetFields();
      console.log('Item form reset successfully');

      // Set default values
      itemForm.setFieldsValue({
        validUntil: dayjs().add(7, 'day'),
        maxPlayCount: 999999999,
        contentType: 'video',
      });
      console.log('Item form values set successfully with defaults:', {
        validUntil: dayjs().add(7, 'day').format(),
        maxPlayCount: 999999999,
        contentType: 'video',
      });
    } catch (error) {
      console.error('Error setting form values:', error);
    }

    setItemModalVisible(true);
    console.log('Modal visible state set to:', true);
  };

  const handleEditItem = (index: number) => {
    setEditingItemIndex(index);
    setEditingItemId(null);
    const item = licenseItems[index];
    itemForm.setFieldsValue({
      fileId: item.fileId,
      contentType: item.contentType,
      validUntil: dayjs(item.validUntil),
      maxPlayCount: item.maxPlayCount,
    });
    setItemModalVisible(true);
  };

  const handleEditServerItem = (item: ApiLicenseItem) => {
    setEditingItemId(item.id);
    setEditingItemIndex(null);
    itemForm.setFieldsValue({
      fileId: item.fileId,
      contentType: item.contentType,
      validUntil: dayjs(item.validUntil),
      maxPlayCount: item.maxPlayCount,
    });
    setItemModalVisible(true);
  };

  const handleBulkGenerate = () => {
    setBulkModalVisible(true);
  };

  const handleBulkSuccess = async (newItems: any[]) => {
    if (isEditMode) {
      message.success(`Successfully added ${newItems.length} license items in bulk`);
      await refetchLicenseItems();
      return;
    }

    setLicenseItems(prevItems => [...prevItems, ...(newItems as DraftLicenseItem[])]);
    message.success(`Successfully added ${newItems.length} license items in bulk`);
  };

  const handleDeleteItem = (index: number) => {
    console.log('Deleting item at index:', index);
    setLicenseItems(items => items.filter((_, i) => i !== index));
    message.success('License item removed successfully');
  };

  const handleDeleteServerItem = async (item: ApiLicenseItem) => {
    try {
      await adminService.deleteLicenseItem(item.id);
      message.success('License item deleted successfully');
      if ((licenseItemsPaged?.data?.length || 0) <= 1 && itemsPage > 1) {
        setItemsPage(itemsPage - 1);
      } else {
        await refetchLicenseItems();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete license item');
    }
  };

  const handleItemModalOk = async () => {
    console.log('Modal OK clicked - processing items');

    if (editingItemId !== null) {
      try {
        const values = await itemForm.validateFields();
        await adminService.updateLicenseItem(editingItemId, {
          fileId: values.fileId,
          contentType: values.contentType,
          validUntil: values.validUntil.format('YYYY-MM-DDTHH:mm:ss'),
          maxPlayCount: values.maxPlayCount,
        });
        message.success('License item updated successfully');
        await refetchLicenseItems();
        setItemModalVisible(false);
        setEditingItemId(null);
        setSelectedMultiItems([]);
        itemForm.resetFields();
      } catch (error: any) {
        console.error('Update license item failed:', error);
        message.error(error.response?.data?.message || 'Failed to update license item');
      }
      return;
    }

    if (editingItemIndex !== null) {
      // For editing mode, keep traditional single-item validation
      console.log('Processing edit mode - validating form');
      itemForm.validateFields().then(values => {
        console.log('Edit validation successful:', values);
        const newItem: DraftLicenseItem = {
          fileId: values.fileId,
          contentType: values.contentType,
          validUntil: values.validUntil.format('YYYY-MM-DDTHH:mm:ss'),
          maxPlayCount: values.maxPlayCount,
          // Note: encryptedKey and signature will be generated server-side
          encryptedKey: undefined,
          signature: undefined,
        };

        console.log('Updating existing item at index:', editingItemIndex);
        setLicenseItems(items => items.map((item, index) =>
          index === editingItemIndex
            ? { ...item, ...newItem }
            : item
        ));
        message.success('License item updated successfully');

        setItemModalVisible(false);
        setEditingItemIndex(null);
        setSelectedMultiItems([]);
        itemForm.resetFields();
        console.log('Edit mode complete');
      }).catch(error => {
        console.error('Form validation failed:', error);
        message.error('Please fill in all required fields');
      });
    } else {
      // For adding mode, use multi-selection with default values
      console.log('Processing multi-add mode');

      if (selectedMultiItems.length === 0) {
        message.warning('Please select at least one content file from the table');
        return;
      }

      // Validate only the default value fields
      try {
        const formValues = await itemForm.validateFields();
        console.log('Multi-add validation successful:', formValues);

        if (isEditMode) {
          const newItems = selectedMultiItems.map(content => ({
            fileId: content.fileId,
            contentType: content.contentType,
            validUntil: formValues.validUntil.format('YYYY-MM-DDTHH:mm:ss'),
            maxPlayCount: formValues.maxPlayCount,
          }));
          await Promise.all(newItems.map((item) => adminService.createLicenseItem(licenseId!, item)));
          message.success(`Successfully added ${newItems.length} license item(s)`);
          await refetchLicenseItems();
        } else {
          // Create license items from selected content with default values
          const newItems: DraftLicenseItem[] = selectedMultiItems.map(content => ({
            id: undefined,
            fileId: content.fileId,
            contentType: content.contentType,
            validUntil: formValues.validUntil.format('YYYY-MM-DDTHH:mm:ss'),
            maxPlayCount: formValues.maxPlayCount,
            // Note: encryptedKey and signature will be generated server-side
            encryptedKey: undefined,
            signature: undefined,
          }));

          console.log('Adding new license items:', newItems);
          setLicenseItems(items => [...items, ...newItems]);
          message.success(`Successfully added ${newItems.length} license item(s)`);
        }

        // Clean up
        setItemModalVisible(false);
        setSelectedMultiItems([]);
        itemForm.resetFields();
        console.log('Multi-add complete');

      } catch (error) {
        console.error('Default values validation failed:', error);
        message.error('Please check default values for license items');
      }
    }
  };

  const handleFormFinish = async (values: any) => {
    console.log('Form finish triggered with values:', values);
    console.log('Current licenseItems:', licenseItems);

    if (!isEditMode && licenseItems.length === 0) {
      message.error('Please add at least one license item (file)');
      return;
    }

    const validUntil = values.validUntil.format('YYYY-MM-DDTHH:mm:ss');
    const licenseData: CreateLicenseData | null = isEditMode ? null : {
      userId: values.userId,
      validUntil,
      maxDevices: values.maxDevices,
      maxConcurrentPlays: values.maxConcurrentPlays,
      maxPlayCount: values.maxPlayCount,
      description: values.description,
      fileIds: licenseItems.map(item => item.fileId),
    };

    if (licenseData) console.log('Prepared license data:', licenseData);

    try {
      if (isEditMode) {
        const updateData: UpdateLicenseData = {
          validUntil,
          maxDevices: values.maxDevices,
          maxConcurrentPlays: values.maxConcurrentPlays,
          maxPlayCount: values.maxPlayCount,
          description: values.description,
        };

        console.log('Updating license with data:', updateData);
        await updateLicenseMutation.mutateAsync({ id: licenseId!, data: updateData });
        message.success('License updated successfully');
        if (onSuccess) onSuccess();
      } else {
        console.log('Creating new license with data:', licenseData);
        await createLicenseMutation.mutateAsync(licenseData!);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      // Error handling is now properly done in mutations with user feedback
    }
  };

  // Create a map of content by fileId for faster lookup
  const contentMap = React.useMemo(() => {
    if (!contentsResponse?.data) return new Map();
    return new Map(contentsResponse.data.map(content => [content.fileId, content]));
  }, [contentsResponse]);

  const columns = [
    {
      title: 'Content File',
      dataIndex: 'fileId',
      key: 'fileId',
      width: 200,
      ellipsis: true,
      render: (fileId: string) => {
        const content = contentMap.get(fileId);
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {content ? content.filename : fileId}
            </div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              ID: {fileId}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Content Type',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 120,
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Valid Until',
      dataIndex: 'validUntil',
      key: 'validUntil',
      width: 130,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Max Play Count',
      dataIndex: 'maxPlayCount',
      key: 'maxPlayCount',
      width: 140,
      render: (count: number) => count.toLocaleString(),
    },
    {
      title: 'Encrypted Key',
      dataIndex: 'encryptedKey',
      key: 'encryptedKey',
      width: 200,
      render: (key: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>
          {key ? `${key.substring(0, 20)}...` : <span style={{ color: '#ccc' }}>To be generated</span>}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: string, record: any, index: number) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => (isEditMode ? handleEditServerItem(record as ApiLicenseItem) : handleEditItem(index))}
          >
            Edit
          </Button>
          <Popconfirm
            title="确定要删除这个许可证项吗？"
            onConfirm={() => (isEditMode ? handleDeleteServerItem(record as ApiLicenseItem) : handleDeleteItem(index))}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isEditMode && licenseLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading license details...</div>
      </div>
    );
  }

  return (
    <div>
      <Card title={
        <Space>
          <SafetyOutlined />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode ? 'Edit License' : 'Create New License'}
          </Title>
        </Space>
      }>
        <Form
          form={form}
          onFinish={handleFormFinish}
          layout="vertical"
          initialValues={{
            maxDevices: 3,
            maxConcurrentPlays: 1,
            maxPlayCount: 999999999,
            validUntil: dayjs().add(7, 'day'),
          }}
        >
          <Row gutter={[24, 24]}>
            <Col span={isEditMode ? 16 : 24}>
              <Title level={4}>License Information</Title>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="User"
                    name="userId"
                    rules={[{ required: true, message: 'Please select a user' }]}
                  >
                    <Select
                      placeholder="Select user"
                      showSearch
                      optionFilterProp="children"
                      disabled={!!isEditMode}
                      loading={!users}
                    >
                      {users && users.map((user) => (
                        <Option key={user.id} value={user.id}>
                          {user.username} - {user.email}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Valid Until"
                    name="validUntil"
                    rules={[{ required: true, message: 'Please select expiration date' }]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      showTime
                      format="YYYY-MM-DD HH:mm"
                      disabledDate={(current) => current && current < dayjs().startOf('day')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Max Devices"
                    name="maxDevices"
                    rules={[{ required: true, type: 'number', min: 1 }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={100}
                      placeholder="Maximum devices"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Concurrent Plays"
                    name="maxConcurrentPlays"
                    rules={[{ required: true, type: 'number', min: 1 }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={10}
                      placeholder="Concurrent plays"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Max Play Count"
                    name="maxPlayCount"
                    rules={[{ required: true, type: 'number', min: 1 }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      placeholder="Max play count"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="Description"
                    name="description"
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder="License description or notes"
                      maxLength={500}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div style={{ marginTop: 32 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                  <Col>
                    <Title level={4} style={{ margin: 0 }}>
                      License Items ({isEditMode ? (licenseItemsPaged?.total || 0) : licenseItems.length} files)
                    </Title>
                  </Col>
                  <Col>
                    <Space>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddItem}
                      >
                        Add Item
                      </Button>
                      {isEditMode && (
                        <Button
                          icon={<FilterOutlined />}
                          onClick={handleBulkGenerate}
                        >
                          Bulk Generate
                        </Button>
                      )}
                      {isEditMode && (
                        <Popconfirm
                          title="确定要清空所有许可证项吗？"
                          description="该操作会删除该 License 下的所有 License Items，且不可撤销。"
                          onConfirm={() => clearLicenseItemsMutation.mutate(licenseId!)}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            disabled={(licenseItemsPaged?.total || 0) === 0}
                            loading={clearLicenseItemsMutation.isPending}
                          >
                            清空所有项
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </Col>
                </Row>

                <Table
                  dataSource={isEditMode ? (licenseItemsPaged?.data || []) : licenseItems}
                  columns={columns}
                  rowKey={(record: any) => (record.id ? String(record.id) : record.fileId)}
                  loading={isEditMode ? licenseItemsLoading : false}
                  pagination={
                    isEditMode
                      ? {
                          current: itemsPage,
                          pageSize: itemsPageSize,
                          total: licenseItemsPaged?.total || 0,
                          showSizeChanger: true,
                          onChange: (p, ps) => {
                            setItemsPage(p);
                            setItemsPageSize(ps);
                          },
                        }
                      : { pageSize: 5 }
                  }
                  locale={{
                    emptyText: 'No license items. Click "Add File" to add files to this license.',
                  }}
                />
              </div>
            </Col>

            {isEditMode && existingLicenseData && (
              <Col span={8}>
                <Card title="Current License Status" style={{ height: '100%' }}>
                  <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label="License ID">
                      {existingLicenseData.id}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">
                      {dayjs(existingLicenseData.createdAt).format('YYYY-MM-DD HH:mm')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Updated">
                      {dayjs(existingLicenseData.updatedAt).format('YYYY-MM-DD HH:mm')}
                    </Descriptions.Item>
                    <Descriptions.Item label="User">
                      {existingLicenseData.user.username}
                    </Descriptions.Item>
                    <Descriptions.Item label="User Email">
                      {existingLicenseData.user.email}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Items">
                      <Tag color="blue">{licenseItemsPaged?.total ?? existingLicenseData.itemsCount ?? 0} files</Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            )}
          </Row>
        </Form>

        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={() => onCancel ? onCancel() : message.info('No cancel callback provided')}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => form.submit()}
              loading={createLicenseMutation.isPending || updateLicenseMutation.isPending}
            >
              {isEditMode ? 'Update License' : 'Create License'}
            </Button>
          </Space>
        </Row>
      </Card>

      {/* License Item Modal - Multi-Selection Mode */}
      <Modal
        title={(editingItemIndex !== null || editingItemId !== null) ? 'Edit License Item' : 'Add Multiple License Items'}
        open={itemModalVisible}
        onOk={handleItemModalOk}
        onCancel={() => {
          console.log('Modal cancelled - resetting state');
          setItemModalVisible(false);
          setEditingItemIndex(null);
          setEditingItemId(null);
          setSelectedMultiItems([]);
          itemForm.resetFields();
        }}
        okText={(editingItemIndex !== null || editingItemId !== null) ? 'Update' : 'Add Selected Items'}
        afterClose={() => {
          console.log('Modal afterClose fired - resetting multi-selection');
          setSelectedMultiItems([]);
          setEditingItemId(null);
          itemForm.resetFields();
        }}
        maskClosable={true}
        destroyOnClose={true}
        getContainer={() => document.body}
        width={1000}
      >
        <Form
          form={itemForm}
          layout="vertical"
        >
          {(editingItemIndex !== null || editingItemId !== null) ? (
            // Edit mode - keep existing single-item form
            <>
              <Form.Item
                label="Content File"
                name="fileId"
                rules={[{ required: true, message: 'Please select a content file' }]}
              >
                <Select
                  placeholder="Select content file"
                  showSearch
                  optionFilterProp="children"
                  onChange={(fileId) => {
                    itemForm.setFieldsValue({ fileId });
                    // Auto-set content type based on selection
                    const selectedContent = contentsResponse?.data?.find((c: Content) => c.fileId === fileId);
                    if (selectedContent) {
                      itemForm.setFieldsValue({ contentType: selectedContent.contentType });
                    }
                  }}
                  value={itemForm.getFieldValue('fileId')}
                >
                  {contentsResponse?.data?.map((content: Content) => (
                    <Option key={content.fileId} value={content.fileId}>
                      {content.filename} - {content.fileId}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Content Type"
                name="contentType"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select content type">
                  <Option value="video">Video</Option>
                  <Option value="audio">Audio</Option>
                  <Option value="document">Document</Option>
                </Select>
              </Form.Item>
            </>
          ) : (
            // Add mode - multi-selection interface
            <>
              <div style={{ marginBottom: 16 }}>
                <h4>Default Values for All Items</h4>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Valid Until"
                      name="validUntil"
                      initialValue={dayjs().add(7, 'day')}
                      rules={[{ required: true, message: 'Please select expiration date' }]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        showTime
                        format="YYYY-MM-DD HH:mm"
                        disabledDate={(current) => current && current < dayjs().startOf('day')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Max Play Count"
                      name="maxPlayCount"
                      initialValue={999999999}
                      rules={[{ required: true, type: 'number', min: 1 }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        placeholder="Maximum play count"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <Divider />

              <div style={{ marginBottom: 16 }}>
                <h4>Select Content Files</h4>
                <p style={{ color: '#666' }}>
                  {selectedMultiItems.length > 0
                    ? `${selectedMultiItems.length} item${selectedMultiItems.length !== 1 ? 's' : ''} selected`
                    : 'No items selected. Use the checkboxes below to select content.'
                  }
                </p>
              </div>

              <ContentSelectorMulti
                onSelect={(fileIds, contents) => {
                  setSelectedMultiItems(contents);
                }}
                multipleSelectionLimit={50}
              />

              {selectedMultiItems.length > 0 && (
                <Alert
                  message="Ready to add items"
                  description={`${selectedMultiItems.length} license item${selectedMultiItems.length !== 1 ? 's' : ''} will be created with the default values above.`}
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </>
          )}
        </Form>
      </Modal>

      {/* Bulk Generation Modal */}
      {isEditMode && (
        <BulkGenerationModal
          visible={bulkModalVisible}
          licenseId={licenseId!}
          onCancel={() => setBulkModalVisible(false)}
          onSuccess={handleBulkSuccess}
          availableContents={contentsResponse?.data || []}
        />
      )}
    </div>
  );
};

export default LicenseEdit;
